import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export interface ICustomErrorHandler {
  handleError(error: any): boolean;
}

export interface IResponseCustomErrorInterface {
  code: string;
  message: string;
  status?: number;
  properties?: any;
  errors?: any;
  details?: any;
}

export class CustomError extends Error implements IResponseCustomErrorInterface {
  code: string;
  status?: number;
  properties?: any;
  errors?: any;
  details?: any;

  constructor(message: string, code: string, status?: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerService {
  private handlerCollection: Array<ICustomErrorHandler> = new Array<ICustomErrorHandler>();
  constructor(private translateService: TranslateService) {}

  public registerErrorHandler(handler: ICustomErrorHandler) {
    this.handlerCollection.push(handler);
  }

  public handle(error: any): boolean {
    return this.handlerCollection.some((handler) => handler.handleError(error));
  }

  public hasError(error: any | null, code: number) {
    return this.checkCode(error, code);
  }

  public getMessage(error: any | null, code: number = null, getOriginMessage = false) {
    return this.getError(error, code, getOriginMessage)?.message;
  }

  public getCode(error: any | null) {
    return this.getError(error)?.code;
  }

  public getError(error: any | null, code: number = null, getOriginMessage = false, status?: number): IResponseCustomErrorInterface {
    if (!error) {
      return null;
    }

    if (error.status) {
      status = error.status;
    }

    if (error.error) {
      return this.getError(error.error, code, getOriginMessage, status);
    }

    if (!this.checkCode(error, code)) {
      return null;
    }

    return this.createError(error, getOriginMessage, status);
  }

  private checkCode(error: any, code: number): boolean {
    if (error?.error) {
      return this.checkCode(error.error, code);
    }

    return error && error.code !== undefined && (code === null || error.code === code);
  }

  private createError(error, getOriginMessage, status) {
    const translateErrorKey = `errors.${error.code}`;
    const translatedMessage = this.translateService.instant(translateErrorKey);

    return Object.assign(
      {
        status: status,
        code: error.code.toString(),
        message: getOriginMessage || translatedMessage === translateErrorKey ? error.message : translatedMessage,
        properties: error.properties ?? null
      },
      error.errors ?? null,
      error.details ?? null
    );
  }

  public isExtSoapError(error: HttpErrorResponse) {
    const m = this.getMessage(error.error);
    return m.startsWith('Internal ext-soap error');
  }
}
